import React, { useRef, Children } from 'react';
import { Button } from './ui/Button';
import { Printer, MapPin, Phone, Globe } from 'lucide-react';
type CertificateProps = {
  titleEn: string;
  titleUa: string;
  subtitleEn?: string;
  subtitleUa?: string;
  isMonetary?: boolean;
};
function CertificateCard({
  titleEn,
  titleUa,
  subtitleEn,
  subtitleUa,
  isMonetary
}: CertificateProps) {
  return <div className="certificate-item relative bg-white text-gray-800 overflow-hidden print:border-none border border-gray-200 shadow-sm print:shadow-none mb-8 print:mb-0" data-id="element-200">
      {/* Dimensions for screen (approx A5 ratio) and print (exact half A4) */}
      <div className="w-full aspect-[1.414/1] print:aspect-auto print:h-[148.5mm] print:w-[210mm] relative flex flex-col" data-id="element-201">
        {/* Decorative Border */}
        <div className="absolute inset-0 p-4 pointer-events-none z-0" data-id="element-202">
          <div className="w-full h-full border-4 border-double border-teal-900/20 rounded-sm" data-id="element-203">
            <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-teal-600 rounded-tl-xl" data-id="element-204" />
            <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-teal-600 rounded-tr-xl" data-id="element-205" />
            <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-teal-600 rounded-bl-xl" data-id="element-206" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-teal-600 rounded-br-xl" data-id="element-207" />
          </div>
        </div>

        {/* Elegant Watermark — Stylized Tooth */}
        <div className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center overflow-hidden" data-id="element-208">
          <svg width="280" height="320" viewBox="0 0 280 320" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-[0.035]" data-id="element-209">
            {/* Tooth crown */}
            <path d="M140 20 C90 20 50 55 50 100 C50 130 60 150 75 165 C85 175 90 190 88 210 L82 280 C80 295 90 310 105 310 C115 310 122 302 125 290 L140 240 L155 290 C158 302 165 310 175 310 C190 310 200 295 198 280 L192 210 C190 190 195 175 205 165 C220 150 230 130 230 100 C230 55 190 20 140 20 Z" fill="currentColor" className="text-teal-700" data-id="element-210" />
            {/* Tooth roots */}
            <path d="M105 310 C100 310 96 305 97 300 L82 280" stroke="currentColor" strokeWidth="2" className="text-teal-700" fill="none" data-id="element-211" />
            <path d="M175 310 C180 310 184 305 183 300 L198 280" stroke="currentColor" strokeWidth="2" className="text-teal-700" fill="none" data-id="element-212" />
            {/* Shine highlight on crown */}
            <path d="M110 60 C115 50 130 45 140 48" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-teal-500" opacity="0.5" fill="none" data-id="element-213" />
            {/* Subtle cross line on crown */}
            <path d="M70 105 Q140 125 210 105" stroke="currentColor" strokeWidth="1.5" className="text-teal-600" opacity="0.4" fill="none" data-id="element-214" />
          </svg>
        </div>

        {/* Elegant Background Pattern */}
        <div className="absolute inset-0 pointer-events-none z-0 opacity-[0.04]" data-id="element-215">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-216">
            <defs data-id="element-217">
              <pattern id="cert-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-218">
                <path d="M20 0 L40 20 L20 40 L0 20 Z" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-teal-800" data-id="element-219" />
                <circle cx="20" cy="20" r="1.5" fill="currentColor" className="text-teal-700" data-id="element-220" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cert-pattern)" data-id="element-221" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 flex-grow flex flex-col p-8 sm:p-10 print:p-8 h-full justify-between" data-id="element-222">
          {/* Header */}
          <div className="flex justify-between items-start" data-id="element-223">
            <div className="flex items-center gap-3" data-id="element-224">
              <img src="https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png" alt="Dentico Clinic Logo" className="h-12 sm:h-14 w-auto object-contain" data-id="element-225" />
              <div className="border-l border-gray-300 pl-3 ml-1" data-id="element-226">
                <span className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight block leading-none mb-1" data-id="element-227">
                  Dentico<span className="text-teal-600" data-id="element-228">Clinic</span>
                </span>
                <p className="text-[11px] sm:text-xs text-gray-600 uppercase tracking-widest leading-tight" data-id="element-229">
                  Private Dentistry
                </p>
                <p className="text-[11px] sm:text-xs text-gray-600 uppercase tracking-widest leading-tight" data-id="element-230">
                  London, Angel
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-231">
              <h1 className="text-xl sm:text-2xl font-serif text-teal-900 font-bold tracking-wide uppercase" data-id="element-232">
                {isMonetary ? 'Gift Voucher' : 'Gift Certificate'}
              </h1>
              <p className="text-sm text-teal-700 font-medium italic" data-id="element-233">
                {isMonetary ? 'Подарунковий ваучер' : 'Подарунковий сертифікат'}
              </p>
            </div>
          </div>

          {/* Main Body */}
          <div className="flex-grow flex flex-col justify-center py-2" data-id="element-234">
            {/* Service Name or Monetary Amount */}
            <div className="border-b-2 border-teal-100 pb-4 mb-4" data-id="element-235">
              {isMonetary ? <div data-id="element-236">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-237">
                    Value / Сума
                  </p>
                  <div className="flex items-baseline text-3xl font-serif font-bold text-teal-900" data-id="element-238">
                    <span data-id="element-239">£</span>
                    <div contentEditable suppressContentEditableWarning className="editable-field min-w-[120px] border-b-2 border-dashed border-teal-300 focus:outline-none focus:border-teal-500 ml-1 px-1 text-teal-900" data-id="element-240" />
                  </div>
                </div> : <>
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-241">
                    Service / Послуга
                  </p>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight mb-1" data-id="element-242">
                    {titleEn}
                  </h2>
                  <p className="text-sm text-gray-600 italic" data-id="element-243">{titleUa}</p>
                  {(subtitleEn || subtitleUa) && <div className="mt-1 text-xs text-teal-700 font-medium" data-id="element-244">
                      {subtitleEn} {subtitleUa && `/ ${subtitleUa}`}
                    </div>}
                </>}
            </div>

            {/* Editable Fields Grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-id="element-245">
              {/* To */}
              <div data-id="element-246">
                <label className="block text-[11px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-247">
                  Presented to / Кому
                </label>
                <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b-2 border-dashed border-gray-300 text-base font-serif text-gray-900 focus:outline-none focus:border-teal-500 pb-1 min-h-[1.6em]" data-id="element-248" />
              </div>

              {/* From */}
              <div data-id="element-249">
                <label className="block text-[11px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-250">
                  From / Від кого
                </label>
                <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b-2 border-dashed border-gray-300 text-base font-serif text-gray-900 focus:outline-none focus:border-teal-500 pb-1 min-h-[1.6em]" data-id="element-251" />
              </div>

              {/* Date & Valid */}
              <div className="flex gap-4 col-span-2 sm:col-span-1" data-id="element-252">
                <div className="flex-1" data-id="element-253">
                  <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-254">
                    Date / Дата
                  </label>
                  <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b border-dashed border-gray-300 text-sm font-mono text-gray-700 focus:outline-none focus:border-teal-500 pb-0.5 min-h-[1.5em]" data-id="element-255" />
                </div>
                <div className="flex-1" data-id="element-256">
                  <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-257">
                    Valid Until / Дійсний до
                  </label>
                  <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b border-dashed border-gray-300 text-sm font-mono text-gray-700 focus:outline-none focus:border-teal-500 pb-0.5 min-h-[1.5em]" data-id="element-258" />
                </div>
              </div>

              {/* Certificate No */}
              <div className="col-span-2 sm:col-span-1" data-id="element-259">
                <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium" data-id="element-260">
                  Certificate No. / Номер
                </label>
                <div contentEditable suppressContentEditableWarning className="editable-field w-full border-b border-dashed border-gray-300 text-sm font-mono text-gray-700 focus:outline-none focus:border-teal-500 pb-0.5 min-h-[1.5em]" data-id="element-261">
                  DC-{new Date().getFullYear()}-
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-end gap-4" data-id="element-262">
            {/* Contact Info */}
            <div className="text-[11px] text-gray-600 space-y-0.5" data-id="element-263">
              <div className="flex items-center gap-1.5" data-id="element-264">
                <MapPin className="w-3 h-3 text-teal-600 flex-shrink-0" data-id="element-265" />
                <span data-id="element-266">360–364 City Road, London EC1V 2PY</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-267">
                <Phone className="w-3 h-3 text-teal-600 flex-shrink-0" data-id="element-268" />
                <span data-id="element-269">+44 7345 197820 (WhatsApp)</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-270">
                <Globe className="w-3 h-3 text-teal-600 flex-shrink-0" data-id="element-271" />
                <span data-id="element-272">denticoclinic.co.uk</span>
              </div>
            </div>

            {/* Terms */}
            <div className="text-[10px] text-gray-500 text-right max-w-[220px] leading-snug" data-id="element-273">
              <p data-id="element-274">
                Valid for one visit. Non-transferable. Please present this
                certificate at your appointment.
              </p>
              <p className="mt-0.5" data-id="element-275">
                Book: denticoclinic.patientportal.onl/bookings
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export function GiftCertificate() {
  const handlePrint = () => {
    window.print();
  };
  const certificates = [{
    titleEn: 'Professional Dental Hygiene',
    titleUa: 'Професійна гігієна: чистка + Airflow',
    subtitleEn: 'Scale & Polish + Airflow'
  }, {
    titleEn: 'Clinical Teeth Whitening',
    titleUa: 'Клінічне відбілювання зубів'
  }, {
    titleEn: 'Hygiene + Whitening Combo',
    titleUa: 'Чистка + Airflow + відбілювання',
    subtitleEn: 'Scale & Polish + Airflow + Whitening'
  }, {
    titleEn: 'Home Whitening Kit',
    titleUa: 'Домашній набір для відбілювання'
  }, {
    titleEn: 'Composite Bonding',
    titleUa: 'Композитний бондинг'
  }, {
    titleEn: 'E.max Veneer (per tooth)',
    titleUa: 'Вінір E.max (1 зуб)'
  }, {
    titleEn: 'Smile Makeover Consultation',
    titleUa: 'Консультація Smile Makeover'
  }, {
    titleEn: 'Hydrafacial',
    titleUa: 'Гідрафейшл'
  }, {
    titleEn: 'Botox Treatment',
    titleUa: 'Процедура ботокс'
  }, {
    titleEn: 'New Patient Exam (incl. 2 X-Rays)',
    titleUa: 'Огляд нового пацієнта (вкл. 2 знімки)'
  }, {
    titleEn: "Children's Dental Hygiene + Fluoride",
    titleUa: 'Дитяча гігієна + фторування'
  }, {
    titleEn: 'Gift Voucher',
    titleUa: 'Подарунковий ваучер',
    isMonetary: true
  }];
  return <div className="min-h-screen bg-gray-100 p-4 sm:p-8 print:p-0 print:bg-white" data-id="element-276">
      {/* Controls - Hidden when printing */}
      <div className="max-w-5xl mx-auto mb-8 flex flex-col sm:flex-row gap-4 items-center justify-between print:hidden" data-id="element-277">
        <div data-id="element-278">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-279">
            Gift Certificates Template
          </h1>
          <p className="text-gray-600 text-sm" data-id="element-280">
            Ready to print. Layout is optimized for 2 certificates per A4 page.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2" data-id="element-281">
          <Button onClick={handlePrint} className="gap-2 shadow-lg" data-id="element-282">
            <Printer className="w-4 h-4" data-id="element-283" />
            Print All Certificates
          </Button>
          <p className="text-xs text-gray-500" data-id="element-284">
            Tip: Set paper size to <strong data-id="element-285">A4</strong> and enable{' '}
            <strong data-id="element-286">Background graphics</strong>
          </p>
        </div>
      </div>

      {/* Certificates Grid */}
      <div className="max-w-[210mm] mx-auto print:max-w-none print:w-full" data-id="element-287">
        {certificates.map((cert, idx) => <CertificateCard key={idx} {...cert} data-id="element-288" />)}
      </div>

      {/* Print Styles */}
      <style data-id="element-289">{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: white;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:border-none {
            border: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:bg-white {
            background: white !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
          .print\\:w-full {
            width: 100% !important;
          }
          
          /* Ensure exactly 2 per page */
          .certificate-item {
            break-inside: avoid;
            page-break-inside: avoid;
            height: 148.5mm; /* Exact half of A4 height */
            width: 210mm;    /* Exact A4 width */
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
          }
          
          /* Force page break after every 2nd item if needed, 
             though natural flow should handle it with fixed height */
          .certificate-item:nth-child(2n) {
            page-break-after: always;
          }
        }
      `}</style>
    </div>;
}